import React, { useRef } from 'react';
import { usePlazasQuery } from '../../services/hooks/usePlazas';
import { Loader2, X, Printer, Download } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Plaza } from '../../types/plaza';
import { toast } from 'react-toastify';

interface ImprimirPlazasProps {
    onClose: () => void;
}

// Interfaz para el agrupamiento de plazas
interface PlazaAgrupada {
    dia: string;
    horaInicio: string;
    nivelId: string | undefined; // Permitir undefined
    nivelNombre: string;
    clientes: { 
        id: string; 
        nombre: string;
        enEspera?: boolean; // Añadir estado de espera
    }[];
}

const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

const ImprimirPlazas: React.FC<ImprimirPlazasProps> = ({ onClose }) => {
    const { data: plazas = [], isLoading, error } = usePlazasQuery();
    const printRef = useRef<HTMLDivElement>(null);

    // Filtrar solo plazas activas
    const plazasActivas = plazas.filter((plaza: Plaza) => plaza.activo);

    // Agrupar plazas por día, hora y nivel
    const plazasAgrupadas: Record<string, Record<string, PlazaAgrupada>> = {};
    
    // Inicializar la estructura para cada día
    diasSemana.forEach(dia => {
        plazasAgrupadas[dia] = {};
    });

    // Agrupar las plazas
    plazasActivas.forEach((plaza: Plaza) => {
        const dia = plaza.dia;
        if (plaza.nivel_id && plaza.horaInicio) {
            const nivelId = typeof plaza.nivel_id === 'object' ? plaza.nivel_id._id : plaza.nivel_id;
            const nivelNombre = typeof plaza.nivel_id === 'object' ? plaza.nivel_id.nombre : 'Sin nivel';
            
            // Asegurarnos de que nivelId sea una cadena
            const key = `${plaza.horaInicio}-${nivelId}`;
            
            if (!plazasAgrupadas[dia][key]) {
                plazasAgrupadas[dia][key] = {
                    dia,
                    horaInicio: plaza.horaInicio,
                    nivelId: nivelId || '', // Garantizar que siempre sea string
                    nivelNombre: nivelNombre,
                    clientes: []
                };
            }
            
            // Añadir cliente si existe
            if (plaza.cliente_id) {
                if (typeof plaza.cliente_id === 'object' && plaza.cliente_id._id) {
                    plazasAgrupadas[dia][key].clientes.push({
                        id: plaza.cliente_id._id,
                        nombre: typeof plaza.cliente_id === 'object' ? plaza.cliente_id.nombre : 'Sin nombre',
                        enEspera: plaza.enEspera // Añadir el estado de espera
                    });
                }
            }
        }
    });

    // Ordenar por hora
    const ordenarPorHora = (grupos: Record<string, PlazaAgrupada>) => {
        return Object.values(grupos).sort((a, b) => {
            // Convertir hora (formato "09:00") a minutos para comparar
            const [horaA, minA] = a.horaInicio.split(':').map(Number);
            const [horaB, minB] = b.horaInicio.split(':').map(Number);
            
            const minutosA = horaA * 60 + minA;
            const minutosB = horaB * 60 + minB;
            
            return minutosA - minutosB;
        });
    };

    // Configurar la impresión
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: 'Plazas_Semanales',
    });

    // Exportar a PDF con gestión inteligente de desbordamiento
    const handleExportPDF = async () => {
        try {
            // Crear PDF en formato horizontal
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Definir límites de la página
            const pageHeight = pdf.internal.pageSize.height;
            const pageWidth = pdf.internal.pageSize.width;
            const margin = 10;
            const startY = 35;
            const colWidth = 58;
            const rowHeight = 10;
            const maxY = pageHeight - 20; // Reservar espacio para pie de página
            
            // Obtener todos los horarios únicos y ordenarlos
            const todosLosHorarios = new Set<string>();
            diasSemana.forEach(dia => {
                Object.values(plazasAgrupadas[dia]).forEach(grupo => {
                    todosLosHorarios.add(grupo.horaInicio);
                });
            });
            
            const horariosOrdenados = Array.from(todosLosHorarios).sort((a, b) => {
                const [horaA, minA] = a.split(':').map(Number);
                const [horaB, minB] = b.split(':').map(Number);
                return (horaA * 60 + minA) - (horaB * 60 + minB);
            });

            // Planificación de páginas
            // En lugar de agrupar por horarios fijos, calculamos dinámicamente lo que cabe en cada página
            let currentPage = 0;
            let currentHorarioIndex = 0;
            
            // Crear primera página
            crearEncabezadoPagina(pdf, currentPage + 1);

            // Para cada horario, intentamos colocarlo en la página actual
            while (currentHorarioIndex < horariosOrdenados.length) {
                const horariosEnEstaPagina: string[] = [];
                let alturaActual = startY + rowHeight; // Comenzamos debajo del encabezado
                let maxAlturaEstaPagina = startY + rowHeight;
                let horariosParaLaSiguientePagina: string[] = [];
                
                // Calcular cuántos horarios caben en esta página
                while (currentHorarioIndex < horariosOrdenados.length) {
                    const horarioActual = horariosOrdenados[currentHorarioIndex];
                    
                    // Calcular altura que ocuparía este horario en la columna más alta
                    const alturaRequerida = calcularAlturaHorario(horarioActual);
                    
                    // Si agregar este horario excedería el límite de la página...
                    if (maxAlturaEstaPagina + alturaRequerida > maxY) {
                        // Si no hemos agregado ningún horario a esta página todavía,
                        // forzamos al menos uno para evitar páginas en blanco
                        if (horariosEnEstaPagina.length === 0) {
                            horariosEnEstaPagina.push(horarioActual);
                            currentHorarioIndex++;
                        }
                        break; // No cabe más en esta página
                    }
                    
                    // Este horario cabe en la página actual
                    horariosEnEstaPagina.push(horarioActual);
                    maxAlturaEstaPagina += alturaRequerida;
                    currentHorarioIndex++;
                }

                // Ahora dibujamos los horarios de esta página
                dibujarHorariosEnPagina(pdf, horariosEnEstaPagina, currentPage + 1);
                
                // Si quedan más horarios, creamos una nueva página
                if (currentHorarioIndex < horariosOrdenados.length) {
                    currentPage++;
                    pdf.addPage();
                    crearEncabezadoPagina(pdf, currentPage + 1);
                }
            }
            
            pdf.save('Plazas_Semanales.pdf');
            toast.success('PDF generado correctamente.');
            
        } catch (err) {
            console.error('Error al exportar PDF:', err);
            toast.error('Error al exportar PDF. Inténtelo de nuevo.');
        }
        
        // Función para calcular la altura que ocuparía un horario específico
        function calcularAlturaHorario(horario: string): number {
            let maxAltura = 0;
            
            diasSemana.forEach(dia => {
                const grupos = ordenarPorHora(plazasAgrupadas[dia]);
                const gruposFiltrados = grupos.filter(grupo => grupo.horaInicio === horario);
                
                let alturaTotal = 0;
                
                gruposFiltrados.forEach(grupo => {
                    // Altura del encabezado del grupo
                    alturaTotal += 6;
                    
                    // Altura de los clientes
                    if (grupo.clientes.length === 0) {
                        alturaTotal += 5; // "Sin clientes"
                    } else {
                        alturaTotal += grupo.clientes.length * 5; // Cada cliente
                    }
                    
                    // Espacio entre grupos
                    alturaTotal += 2;
                });
                
                maxAltura = Math.max(maxAltura, alturaTotal);
            });
            
            return maxAltura || 10; // Mínimo 10mm si no hay datos
        }
        
        // Función para crear el encabezado de la página
        function crearEncabezadoPagina(pdf: jsPDF, pageNum: number) {
            const margin = 10;
            const startY = 35;
            const colWidth = 58;
            const rowHeight = 10;
            
            // Título de la página
            pdf.setFontSize(16);
            pdf.setFont("helvetica", "bold");
            pdf.text("Plazas Semanales", 149, 15, { align: "center" });
            
            // Encabezados de columnas (días)
            pdf.setFillColor(230, 230, 230);
            pdf.setDrawColor(180, 180, 180);
            
            diasSemana.forEach((dia, index) => {
                // Rectángulo de encabezado
                pdf.setFillColor(230, 230, 230);
                pdf.rect(margin + index * colWidth, startY, colWidth, rowHeight, "FD");
                
                // Texto del día
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(10);
                pdf.text(dia, margin + index * colWidth + colWidth/2, startY + rowHeight/2 + 1, { align: "center" });
            });
            
            // Información de pie de página
            pdf.setFontSize(8);
            pdf.setTextColor(120, 120, 120);
            const today = new Date().toLocaleDateString();
            pdf.text(`Generado el ${today} - Página ${pageNum}`, 149, 200, { align: "center" });
        }
        
        // Función para dibujar los horarios en la página
        function dibujarHorariosEnPagina(pdf: jsPDF, horarios: string[], pageNum: number) {
            const margin = 10;
            const startY = 35;
            const colWidth = 58;
            const rowHeight = 10;
            
            // Subtítulo con los horarios de esta página
            pdf.setFontSize(12);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Horarios: ${horarios.join(", ")}`, 149, 25, { align: "center" });
            
            // Dibujar datos para cada día
            let maxRowsUsed = 0;
            
            diasSemana.forEach((dia, diaIndex) => {
                const grupos = ordenarPorHora(plazasAgrupadas[dia]);
                const gruposFiltrados = grupos.filter(grupo => horarios.includes(grupo.horaInicio));
                
                let currentY = startY + rowHeight; // Iniciar debajo del encabezado
                let rowsUsed = 0;
                
                gruposFiltrados.forEach(grupo => {
                    const clientesOrdenados = [...grupo.clientes].sort((a, b) => 
                        a.nombre.localeCompare(b.nombre)
                    );
                    
                    // Calcular espacio que ocupará este grupo
                    const espacioGrupo = 6 + (clientesOrdenados.length === 0 ? 5 : clientesOrdenados.length * 5) + 2;
                    
                    // Encabezado del grupo
                    pdf.setFont("helvetica", "bold");
                    pdf.setFontSize(9);
                    pdf.setTextColor(0, 0, 0);
                    pdf.text(`${grupo.horaInicio} - ${grupo.nivelNombre}`, margin + diaIndex * colWidth + 2, currentY + 4);
                    currentY += 6;
                    rowsUsed++;
                    
                    // Lista de clientes
                    if (clientesOrdenados.length === 0) {
                        pdf.setFont("helvetica", "normal");
                        pdf.setFontSize(8);
                        pdf.setTextColor(150, 150, 150);
                        pdf.text("Sin clientes", margin + diaIndex * colWidth + 4, currentY + 3);
                        currentY += 5;
                        rowsUsed++;
                    } else {
                        clientesOrdenados.forEach(cliente => {
                            pdf.setTextColor(0, 0, 0);
                            pdf.setFontSize(8);
                            
                            // Establecer estilo para clientes en espera
                            if (cliente.enEspera) {
                                pdf.setFont("helvetica", "bolditalic");
                            } else {
                                pdf.setFont("helvetica", "normal");
                            }
                            
                            // Preparar el nombre con indicador de espera si es necesario
                            let nombreMostrado = cliente.nombre;
                            if (cliente.enEspera) {
                                nombreMostrado += " (en espera)";
                            }
                            
                            // Truncar nombres largos (ajustando para "(en espera)")
                            nombreMostrado = nombreMostrado.length > 30 
                                ? nombreMostrado.substring(0, 28) + "..." 
                                : nombreMostrado;
                            
                            pdf.text("• " + nombreMostrado, margin + diaIndex * colWidth + 4, currentY + 3);
                            currentY += 5;
                            rowsUsed++;
                             // Si está en espera, mostrar la indicación en una línea separada y en rojo
                            if (cliente.enEspera) {
                                pdf.setTextColor(220, 0, 0); // Rojo brillante
                                pdf.setFont("helvetica", "italic");
                                pdf.text("   (en espera)", margin + diaIndex * colWidth + 8, currentY + 1);
                                currentY += 4; // Un poco menos de espacio que una línea completa
                                rowsUsed++;
                            }
                        });
                    }
                    
                    // Espacio entre grupos y línea separadora
                    currentY += 2;
                    rowsUsed++;
                    
                    pdf.setDrawColor(220, 220, 220);
                    pdf.line(
                        margin + diaIndex * colWidth + 2, 
                        currentY - 1, 
                        margin + (diaIndex + 1) * colWidth - 2, 
                        currentY - 1
                    );
                });
                
                // Actualizar máximas filas usadas
                maxRowsUsed = Math.max(maxRowsUsed, rowsUsed);
            });
            
            // Dibujar líneas verticales y horizontales para la tabla
            const finalHeight = startY + rowHeight + (maxRowsUsed * 5);
            
            // Líneas verticales para separar columnas
            diasSemana.forEach((_, diaIndex) => {
                pdf.setDrawColor(180, 180, 180);
                pdf.line(
                    margin + diaIndex * colWidth, 
                    startY, 
                    margin + diaIndex * colWidth, 
                    finalHeight
                );
            });
            
            // Línea vertical final
            pdf.setDrawColor(180, 180, 180);
            pdf.line(
                margin + 5 * colWidth, 
                startY, 
                margin + 5 * colWidth, 
                finalHeight
            );
            
            // Línea horizontal inferior
            pdf.line(
                margin, 
                finalHeight, 
                margin + 5 * colWidth, 
                finalHeight
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-7xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Plazas Semanales</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                ) : error ? (
                    <div className="text-red-500 p-4">Error al cargar las plazas: {String(error)}</div>
                ) : (
                    <>
                        <div className="flex justify-end mb-4 space-x-2">
                            <button 
                                onClick={() => handlePrint()}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center"
                            >
                                <Printer className="w-4 h-4 mr-2" />
                                Imprimir
                            </button>
                            <button 
                                onClick={handleExportPDF}
                                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Exportar PDF
                            </button>
                        </div>

                        {/* Contenido imprimible */}
                        <div ref={printRef} className="p-4 bg-white">
                            <h1 className="text-2xl font-bold text-center mb-6">Plazas Semanales</h1>
                            <div className="grid grid-cols-5 gap-4">
                                {diasSemana.map(dia => (
                                    <div key={dia} className="border rounded-lg p-3">
                                        <h3 className="text-lg font-bold text-center mb-3 bg-gray-100 py-1">{dia}</h3>
                                        <div className="space-y-4">
                                            {ordenarPorHora(plazasAgrupadas[dia]).map((grupo, idx) => (
                                                <div key={idx} className="border-b pb-2">
                                                    <p className="font-semibold">
                                                        {grupo.horaInicio} - {grupo.nivelNombre}
                                                    </p>
                                                    <ul className="mt-1 pl-4">
                                                        {grupo.clientes
                                                            .sort((a, b) => a.nombre.localeCompare(b.nombre))
                                                            .map(cliente => (
                                                                <li 
                                                                    key={cliente.id} 
                                                                    className={`text-sm list-disc ${cliente.enEspera ? 'font-bold italic' : ''}`}
                                                                >
                                                                    {cliente.nombre}{cliente.enEspera ? ' (en espera)' : ''}
                                                                </li>
                                                            ))
                                                        }
                                                        {grupo.clientes.length === 0 && (
                                                            <li className="text-sm italic text-gray-500">Sin clientes</li>
                                                        )}
                                                    </ul>
                                                </div>
                                            ))}
                                            {Object.keys(plazasAgrupadas[dia]).length === 0 && (
                                                <p className="text-sm italic text-gray-500">No hay plazas</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ImprimirPlazas;